// import fb from 'firebase/compat/app';
import { FirebaseApp, FirebaseOptions, initializeApp } from 'firebase/app';
import {
  Firestore,
  CACHE_SIZE_UNLIMITED,
  connectFirestoreEmulator,
  setDoc,
  doc,
  FirestoreSettings,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { getRemoteConfig, RemoteConfig } from 'firebase/remote-config';
import { getValue } from 'firebase/remote-config';
import {
  Auth,
  connectAuthEmulator,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
  User,
} from 'firebase/auth';
import {
  connectFunctionsEmulator,
  Functions,
  getFunctions,
} from 'firebase/functions';
import {
  connectDatabaseEmulator,
  Database,
  getDatabase,
} from 'firebase/database';
import {
  getStorage,
  // connectStorageEmulator,
  FirebaseStorage,
} from 'firebase/storage';

import { Analytics, getAnalytics } from 'firebase/analytics';
//todo: firebase UI requires version 8 compat, console snippets require access to globals
import firebaseCompat from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/functions';
import 'firebase/compat/database';

import { Capacitor } from '@capacitor/core';
import { getRemoteConfigValueForKey } from './native';
import { AuthType } from '@sworkit/api';

export type FirebaseAuthUser = User;

export let firebaseApp: FirebaseApp;
//firestore namespace needs to be protected
export let fst: Firestore;
export let remoteConfig: RemoteConfig;
export let firebaseAuth: Auth;
export let firebaseFunctions: Functions;
export let realtimeDatabase: Database;
export let firebaseAnalytics: Analytics;
export let firebaseStorage: FirebaseStorage;

export let firebaseAuthCompat;
export let cloudFunctionsDomain = `https://us-central1-sworkit-user.cloudfunctions.net`;
export let remoteStorageUrl = 'https://storage.googleapis.com/';
export let assetBucket = 'sworkit-assets';

const config: FirebaseOptions = {
  apiKey: 'AIzaSyDAXD_8yh7cY4lfS4ruUPyRtcY4VbneqNg',
  authDomain: 'sworkit-user.firebaseapp.com',
  databaseURL: 'https://sworkit-user.firebaseio.com',
  projectId: 'sworkit-user',
  messagingSenderId: '267078125942',
  appId: '1:267078125942:web:525b3d123282dfd0149257',
  measurementId: 'G-SETPHB1LCH',
  storageBucket: 'gs://sworkit-assets',
};
const firestoreSettings: FirestoreSettings = {
  experimentalForceLongPolling: true,
  ignoreUndefinedProperties: true,
};

const localCache = persistentLocalCache(
  /*settings*/ {
    tabManager: persistentMultipleTabManager(),
    cacheSizeBytes: CACHE_SIZE_UNLIMITED,
  },
);

export function isInIframe() {
  // only call in firebase.ts
  // instead use ui.isInIframe in redux
  if (window.location !== window.parent.location) {
    console.debug('inside iframe');
    return true;
  } else {
    console.debug('not in iframe');
    return false;
  }
}

export function initializeFirebase() {
  if (!isInIframe()) {
    firebaseApp = initializeApp(config);
    firebaseCompat.initializeApp(config);
    remoteConfig = getRemoteConfig(firebaseApp);
    if (Capacitor.isNativePlatform()) {
      firebaseAuth = initializeAuth(firebaseApp, {
        persistence: indexedDBLocalPersistence,
      });
    } else {
      firebaseAuth = getAuth(firebaseApp);
    }
    firebaseFunctions = getFunctions(firebaseApp);
    realtimeDatabase = getDatabase(firebaseApp);
    firebaseAnalytics = getAnalytics(firebaseApp);
    firebaseStorage = getStorage(firebaseApp);

    startEmulatorOrStartOffline();

    firebaseAuthCompat = firebaseCompat.auth();
    firebaseCompat.firestore();
    (window as any).firebase = firebaseCompat;
  }
  return firebaseApp;
}

function startEmulatorOrStartOffline() {
  //set the firebase emulator app to npm start on port 5555 so check that here
  if (location?.host == 'localhost:5555') {
    console.debug('running on Firebase emulator');
    fst = initializeFirestore(firebaseApp, firestoreSettings);

    connectFirestoreEmulator(fst, 'localhost', 8080);
    connectAuthEmulator(firebaseAuth, 'http://localhost:9099');
    connectFunctionsEmulator(firebaseFunctions, 'localhost', 5001);
    connectDatabaseEmulator(realtimeDatabase, 'localhost', 9000);
    // connectStorageEmulator(firebaseStorage, 'localhost', 9199); //todo: storage environment simulator? all images and workout data???

    //window functions
    (window as any).createDoc = (docPath: string, document: any) => {
      return setDoc(doc(fst, docPath), document);
    };
    cloudFunctionsDomain = `http://localhost:5001`;
  } else {
    initFirestoreOffline();
  }
}

export function initFirestoreOffline() {
  if (!firebaseApp) return;
  fst = initializeFirestore(firebaseApp, { ...firestoreSettings, localCache });
}

export async function getRemoteConfigKey(
  key: string,
  type: 'string',
): Promise<string>;
export async function getRemoteConfigKey(
  key: string,
  type: 'boolean',
): Promise<boolean>;
export async function getRemoteConfigKey(
  key: string,
  type: 'number',
): Promise<number>;
export async function getRemoteConfigKey(
  remoteConfigKey: string,
  type: 'string' | 'boolean' | 'number',
) {
  if (isInIframe()) return;
  switch (type) {
    case 'string': {
      if (Capacitor.isNativePlatform()) {
        return getRemoteConfigValueForKey<string>({
          remoteConfigKey,
          type,
        });
      } else {
        return getValue(remoteConfig, remoteConfigKey).asString() as string;
      }
    }
    case 'boolean': {
      if (Capacitor.isNativePlatform()) {
        return getRemoteConfigValueForKey<boolean>({
          remoteConfigKey,
          type,
        });
      } else {
        return getValue(remoteConfig, remoteConfigKey).asBoolean() as boolean;
      }
    }
    case 'number': {
      if (Capacitor.isNativePlatform()) {
        return getRemoteConfigValueForKey<number>({
          remoteConfigKey,
          type: 'double',
        });
      } else {
        return getValue(remoteConfig, remoteConfigKey).asNumber() as number;
      }
    }
    default:
      return null;
  }
}

export function getAuthTypeFromFirebaseUser(user: FirebaseAuthUser): AuthType {
  if (user && user.uid && user.uid.includes('clever:')) {
    return 'clever';
  }
  if (user?.uid?.includes('virgin-pulse:')) {
    return 'virgin-pulse';
  }
  // if (!user || !user || !user.providerData || !user.providerData[0]) return;
  const providerList = Array.isArray(user?.providerData)
    ? [...user?.providerData]
    : [];
  const provider = providerList?.shift();
  const providerMap: { [key: string]: AuthType } = {
    password: 'email',
    email: 'email',
    google: 'google',
    facebook: 'facebook',
    apple: 'apple',
    'google.com': 'google',
    'facebook.com': 'facebook',
    'apple.com': 'apple',
  };
  if (provider && providerMap[provider.providerId]) {
    return providerMap[provider.providerId];
  }
  if (provider?.providerId) {
    return provider?.providerId as any;
  }

  //would put at the top but there's not many cases where the provider will be solera,
  // as you should be signing in with regular auth
  if (user && user.uid && user.uid.includes('solera:')) {
    return 'solera';
  }
  return 'sworkit' as AuthType;

  // shouldn't get here, but maybe we're missing possible providers
}
